import React from "react";
import { LucideIcon, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";

const KPICard = ({
  title,
  value,
  icon: Icon,
  subValue,
  borderColor,
  baselineValue,
  inverseTrend = false,
  formatFn = (v) => v.toLocaleString(),
}) => {
  const theme = useTheme();

  const getDelta = () => {
    if (baselineValue === undefined || baselineValue === null) return null;
    const currentNum =
      typeof value === "number"
        ? value
        : parseFloat(String(value).replace(/[^0-9.-]+/g, ""));
    const baselineNum =
      typeof baselineValue === "number"
        ? baselineValue
        : parseFloat(String(baselineValue).replace(/[^0-9.-]+/g, ""));
    if (isNaN(currentNum) || isNaN(baselineNum) || baselineNum === 0)
      return null;
    const diff = currentNum - baselineNum;
    const percent = (diff / baselineNum) * 100;
    return { diff, percent };
  };

  const delta = getDelta();

  let TrendIcon = Minus;
  let trendColor = theme.palette.text.secondary;
  let trendBgColor = theme.palette.action.hover;

  if (delta && Math.abs(delta.percent) >= 0.1) {
    const isPositiveChange = delta.diff > 0;
    // inverseTrend: true means "Higher is Bad" (like Loss Ratio)
    const isGood = inverseTrend ? !isPositiveChange : isPositiveChange;

    trendColor = isGood ? theme.palette.success.main : theme.palette.error.main;
    trendBgColor = isGood
      ? alpha(theme.palette.success.main, 0.1)
      : alpha(theme.palette.error.main, 0.1);
    TrendIcon = isPositiveChange ? ArrowUpRight : ArrowDownRight;
  }

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        borderLeft: borderColor ? `4px solid ${borderColor}` : undefined,
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0px 12px 30px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1}
        >
          <Typography
            variant="overline"
            sx={{
              letterSpacing: 1.5,
              fontWeight: 700,
              color: "text.secondary",
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              p: 1,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={20} strokeWidth={2} />
          </Box>
        </Stack>

        <Stack spacing={1}>
          <Stack direction="row" alignItems="baseline" spacing={1.5}>
            <Typography
              variant="h4"
              fontWeight="800"
              color="text.primary"
              sx={{ letterSpacing: -1 }}
            >
              {value}
            </Typography>

            {delta && Math.abs(delta.percent) > 0.1 && (
              <Chip
                icon={<TrendIcon size={14} color={trendColor} />}
                label={`${Math.abs(delta.percent).toFixed(1)}%`}
                size="small"
                sx={{
                  height: 24,
                  bgcolor: trendBgColor,
                  color: trendColor,
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  "& .MuiChip-icon": { color: "inherit" },
                }}
              />
            )}
          </Stack>

          {subValue && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 500, display: "block" }}
            >
              {subValue}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default KPICard;
